import React, { useEffect, useState, useCallback } from 'react';
import { QrCode, Plus, Download, Trash2, BarChart3, Loader2, Upload, Copy, Check } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, inputCls, copyText } from './shared';
import { toast } from 'sonner';

const TYPES = [
  { v: 'url', l: 'Website / Link' },
  { v: 'text', l: 'Plain Text' },
  { v: 'phone', l: 'Phone Call' },
  { v: 'sms', l: 'SMS' },
  { v: 'email', l: 'Email' },
  { v: 'wifi', l: 'WiFi' },
  { v: 'vcard', l: 'Contact Card' },
];

const PALETTES = [
  { dark: '#000000', light: '#ffffff' },
  { dark: '#0a0a0a', light: '#b8ff00' },
  { dark: '#b8ff00', light: '#0a0a0a' },
  { dark: '#1d4ed8', light: '#ffffff' },
  { dark: '#be123c', light: '#fff1f2' },
  { dark: '#065f46', light: '#ecfdf5' },
];

export default function QRStudio({ campaigns = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState({
    name: '', destination: '', qr_type: 'url', track: true, campaign_id: '',
    style: { dark: '#000000', light: '#ffffff', scale: 10, border: 4 }, logo_b64: null,
  });

  const load = useCallback(() => {
    suiteAPI.listQR().then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm(p => ({ ...p, logo_b64: reader.result }));
    reader.readAsDataURL(f);
  };

  const create = async () => {
    if (!form.destination.trim()) return toast.error('Add a destination first');
    setCreating(true);
    try {
      const { data } = await suiteAPI.createQR({ ...form, base_url: window.location.origin });
      setPreview(data);
      toast.success('QR code created');
      setForm(p => ({ ...p, name: '', destination: '' }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create QR code');
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    await suiteAPI.deleteQR(id);
    setItems(p => p.filter(i => i.id !== id));
    if (preview?.id === id) setPreview(null);
  };

  const copyLink = (text, id) => {
    copyText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <Panel title="QR Code Studio" subtitle="Trackable QR codes for print, packaging, events and out-of-home. Every scan is counted and attributed to a campaign." testId="suite-qr">
      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        <Card className="p-5 space-y-4 h-fit">
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map(t => (
              <Chip key={t.v} active={form.qr_type === t.v} onClick={() => setForm({ ...form, qr_type: t.v })} testId={`qr-type-${t.v}`}>{t.l}</Chip>
            ))}
          </div>
          <Field label="Name">
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Spring flyer QR" className={inputCls} data-testid="qr-name" />
          </Field>
          <Field label={form.qr_type === 'url' ? 'Destination URL' : 'Content'} hint={form.qr_type === 'url' ? 'Trackable codes redirect through MMOS so scans are counted.' : undefined}>
            <Input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder={form.qr_type === 'url' ? 'https://yourpage.com/offer' : 'Anything you want encoded'} className={inputCls} data-testid="qr-destination" />
          </Field>
          {form.qr_type === 'url' && (
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={form.track} onChange={e => setForm({ ...form, track: e.target.checked })} data-testid="qr-track" className="accent-[#b8ff00]" />
              Track scans (recommended)
            </label>
          )}
          <Field label="Campaign">
            <select value={form.campaign_id} onChange={e => setForm({ ...form, campaign_id: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2" data-testid="qr-campaign">
              <option value="">No campaign</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Colors">
            <div className="flex flex-wrap gap-2">
              {PALETTES.map((p, i) => (
                <button key={i} onClick={() => setForm({ ...form, style: { ...form.style, ...p } })}
                  data-testid={`qr-palette-${i}`}
                  className={`w-9 h-9 rounded-lg border-2 ${form.style.dark === p.dark && form.style.light === p.light ? 'border-[#b8ff00]' : 'border-zinc-700'}`}
                  style={{ background: `linear-gradient(135deg, ${p.dark} 50%, ${p.light} 50%)` }} />
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Foreground">
              <input type="color" value={form.style.dark} onChange={e => setForm({ ...form, style: { ...form.style, dark: e.target.value } })} className="w-full h-9 bg-zinc-800 rounded" />
            </Field>
            <Field label="Background">
              <input type="color" value={form.style.light} onChange={e => setForm({ ...form, style: { ...form.style, light: e.target.value } })} className="w-full h-9 bg-zinc-800 rounded" />
            </Field>
          </div>
          <Field label="Center logo (optional)">
            <label className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 cursor-pointer hover:border-zinc-600">
              <Upload size={13} /> {form.logo_b64 ? 'Logo added — replace' : 'Upload PNG logo'}
              <input type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="qr-logo" />
            </label>
          </Field>
          <Button onClick={create} disabled={creating} className="w-full bg-[#b8ff00] text-black font-bold" data-testid="qr-create-btn">
            {creating ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Plus size={15} className="mr-1.5" />} Generate QR Code
          </Button>
        </Card>

        <div className="space-y-4">
          {preview && (
            <Card className="p-5 flex flex-col sm:flex-row gap-5 items-center" data-testid="qr-preview">
              <img src={preview.png_data_url} alt="QR" className="w-40 h-40 rounded-lg bg-white p-1" />
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-white font-bold">{preview.name}</p>
                <p className="text-xs text-zinc-500 break-all">{preview.encoded_value}</p>
                <div className="flex gap-2 flex-wrap">
                  <a href={suiteAPI.qrRenderUrl(preview.id, 'png', 20)} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-zinc-800 text-white" data-testid="qr-download-png"><Download size={13} className="mr-1" /> PNG (print)</Button>
                  </a>
                  <a href={suiteAPI.qrRenderUrl(preview.id, 'svg')} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-zinc-800 text-white" data-testid="qr-download-svg"><Download size={13} className="mr-1" /> SVG (vector)</Button>
                  </a>
                  <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => copyLink(preview.encoded_value, preview.id)}>
                    {copied === preview.id ? <Check size={13} /> : <Copy size={13} />}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {loading ? <PanelLoader /> : items.length === 0 ? (
            <EmptyState icon={QrCode} text="No QR codes yet. Generate your first one on the left." />
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map(q => (
                <Card key={q.id} className="p-4" data-testid={`qr-item-${q.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{q.name}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{q.destination}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#b8ff00]/10 text-[#b8ff00] shrink-0">{q.scans} scans</span>
                  </div>
                  <img src={suiteAPI.qrRenderUrl(q.id, 'png', 6)} alt="" className="w-full aspect-square object-contain bg-white rounded-lg mt-3 p-2" />
                  <div className="flex items-center gap-1.5 mt-3">
                    <a href={suiteAPI.qrRenderUrl(q.id, 'png', 20)} target="_blank" rel="noreferrer" className="flex-1">
                      <Button size="sm" className="w-full bg-zinc-800 text-white text-xs"><Download size={12} className="mr-1" /> Download</Button>
                    </a>
                    <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => suiteAPI.qrAnalytics(q.id).then(r => setStats(r.data))} data-testid={`qr-stats-${q.id}`}><BarChart3 size={13} /></Button>
                    <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => remove(q.id)} data-testid={`qr-delete-${q.id}`}><Trash2 size={13} /></Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {stats && (
            <Card className="p-5" data-testid="qr-analytics">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white">{stats.qr.name} — {stats.total_scans} scans</h3>
                <Button size="sm" variant="ghost" className="text-zinc-500" onClick={() => setStats(null)}>Close</Button>
              </div>
              {stats.by_day.length === 0 ? <p className="text-xs text-zinc-500">No scans recorded yet.</p> : (
                <div className="flex items-end gap-1.5 h-24">
                  {stats.by_day.map(d => (
                    <div key={d.date} className="flex-1 bg-[#b8ff00]/70 rounded-t" style={{ height: `${Math.max(8, (d.scans / Math.max(...stats.by_day.map(x => x.scans))) * 100)}%` }} title={`${d.date}: ${d.scans}`} />
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </Panel>
  );
}
